import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, MapPin, LogOut, Clock, FileText, Camera, Navigation, ArrowLeft, History, ChevronLeft, ChevronRight, Info, ArrowUpDown, ArrowUp, ArrowDown, Image as ImageIcon, X, Upload, Trash2, Edit2, Wrench } from "lucide-react";
import RepairBookingTab from "./tabs/RepairBookingTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DATA_RETENTION_TEXT = "Delivery history is stored for 36 days";

// Format date as dd/mm/yyyy
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Format in Singapore timezone (GMT+8)
  return date.toLocaleDateString('en-GB', { 
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric'
  });
};

// Format date with time as dd/mm/yyyy HH:mm in Singapore timezone
const formatDateTimeDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Format in Singapore timezone (GMT+8)
  const datePart = date.toLocaleDateString('en-GB', { 
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric'
  });
  const timePart = date.toLocaleTimeString('en-GB', { 
    timeZone: 'Asia/Singapore',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${datePart} ${timePart}`;
};

export default function DriverDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("deliveries"); // 'deliveries' | 'book-repair'
  const [sites, setSites] = useState([]);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [checkInData, setCheckInData] = useState({
    delivery_notes: "",
  });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historySortConfig, setHistorySortConfig] = useState({ key: 'checkin_time', direction: 'desc' });
  const ITEMS_PER_PAGE = 100;
  const [siteLoads, setSiteLoads] = useState({}); // Map of site_id to remaining loads
  const [siteDumpingGrounds, setSiteDumpingGrounds] = useState({}); // Map of site_id -> dumping ground name(s)
  const [siteSearch, setSiteSearch] = useState(""); // Search filter for sites
  const [selectedPhoto, setSelectedPhoto] = useState(null); // For viewing DO photo
  const photoInputRef = useRef(null); // Ref for photo file input
  const historyPhotoInputRef = useRef(null); // Ref for history photo upload
  const [editingDeliveryPhoto, setEditingDeliveryPhoto] = useState(null); // Delivery being edited for photo
  const [uploadingPhoto, setUploadingPhoto] = useState(false); // Loading state for photo upload

  const [dataLoading, setDataLoading] = useState(true);

  // Filter and sort sites alphabetically
  const filteredSites = sites
    .filter(site => {
      if (!siteSearch) return true;
      const search = siteSearch.toLowerCase();
      return (
        site.name.toLowerCase().includes(search) ||
        (site.customer_name || '').toLowerCase().includes(search)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    fetchData();
    // Poll every 30 seconds for updates (reduced from 5s for better performance)
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      // Fetch sites and active delivery first (critical for initial render)
      const [sitesRes, activeRes] = await Promise.all([
        axios.get(`${API}/sites`),
        axios.get(`${API}/deliveries/active`),
      ]);
      setSites(sitesRes.data);
      setActiveDelivery(activeRes.data);
      setDataLoading(false);
      
      // Fetch non-critical data in background
      const [historyRes, jobsRes] = await Promise.all([
        axios.get(`${API}/deliveries/my-history`),
        axios.get(`${API}/driver/jobs`).catch(() => ({ data: { site_loads: [] } })),
      ]);
      setDeliveryHistory(historyRes.data || []);
      
      // Convert site_loads array to a map for easy lookup
      const loadsMap = {};
      const dgMap = {};
      if (jobsRes.data?.site_loads) {
        jobsRes.data.site_loads.forEach(item => {
          loadsMap[item.site_id] = item.total_remaining;
          if (item.dumping_ground_names && item.dumping_ground_names.length) {
            dgMap[item.site_id] = item.dumping_ground_names.join(', ');
          }
        });
      }
      setSiteLoads(loadsMap);
      setSiteDumpingGrounds(dgMap);
    } catch (error) {
      console.error("Error fetching data:", error);
      setDataLoading(false);
    }
  };

  const fetchActiveDelivery = async () => {
    try {
      const response = await axios.get(`${API}/deliveries/active`);
      setActiveDelivery(response.data);
    } catch (error) {
      console.error("Error fetching active delivery:", error);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!selectedSiteId) {
      toast.error("Please select a site");
      return;
    }
    
    setLoading(true);
    try {
      // Get location with 3 second timeout for speed
      let location = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => reject(new Error('timeout')), 3000);
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                clearTimeout(timeoutId);
                resolve(pos);
              },
              (err) => {
                clearTimeout(timeoutId);
                reject(err);
              },
              { enableHighAccuracy: false, timeout: 3000, maximumAge: 60000 }
            );
          });
          location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        } catch (e) {
          // Continue without location if it fails or times out
          console.log("Location unavailable, proceeding without it");
        }
      }
      
      const response = await axios.post(`${API}/deliveries/checkin`, {
        site_id: selectedSiteId,
        ...checkInData,
        location,
      });
      
      setActiveDelivery(response.data);
      setSelectedSiteId("");
      toast.success("Checked in successfully!");
      setCheckInData({
        delivery_notes: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Compress image to be under 200KB
        const compressedImage = await compressImage(file, 200);
        setPhoto(compressedImage);
        toast.success("Photo uploaded and optimized");
      } catch (error) {
        toast.error("Failed to process image");
        console.error("Image compression error:", error);
      }
    }
  };

  const compressImage = (file, maxSizeKB) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Max dimensions - smaller = faster + smaller file
          const maxDimension = 1024;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round(height * (maxDimension / width));
              width = maxDimension;
            } else {
              width = Math.round(width * (maxDimension / height));
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with quality 0.7 and reduce aggressively if needed
          let quality = 0.7;
          let result = canvas.toDataURL('image/jpeg', quality);
          
          // Iteratively reduce quality until under maxSizeKB
          // Base64 is ~33% larger than binary, so we check length * 0.75
          while (result.length * 0.75 / 1024 > maxSizeKB && quality > 0.1) {
            quality -= 0.1;
            result = canvas.toDataURL('image/jpeg', quality);
          }
          
          // If still too large, reduce dimensions further
          if (result.length * 0.75 / 1024 > maxSizeKB) {
            const scale = Math.sqrt(maxSizeKB / (result.length * 0.75 / 1024));
            canvas.width = Math.round(width * scale);
            canvas.height = Math.round(height * scale);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            result = canvas.toDataURL('image/jpeg', 0.6);
          }
          
          console.log(`Compressed to ${Math.round(result.length * 0.75 / 1024)}KB`);
          resolve(result);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCheckOut = async () => {
    if (!photo) {
      toast.error("Please upload a photo before checking out");
      return;
    }

    setLoading(true);
    try {
      // Get location with 3 second timeout for speed
      let location = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => reject(new Error('timeout')), 3000);
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                clearTimeout(timeoutId);
                resolve(pos);
              },
              (err) => {
                clearTimeout(timeoutId);
                reject(err);
              },
              { enableHighAccuracy: false, timeout: 3000, maximumAge: 60000 }
            );
          });
          location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        } catch (e) {
          // Continue without location if it fails or times out
          console.log("Location unavailable, proceeding without it");
        }
      }

      await axios.patch(`${API}/deliveries/${activeDelivery.id}/checkout`, {
        photo,
        location,
      });

      toast.success("Checked out successfully!");
      setActiveDelivery(null);
      setPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-out failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelivery = async () => {
    if (!window.confirm("Are you sure you want to cancel this delivery? This will remove your check-in.")) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/deliveries/${activeDelivery.id}/cancel`);
      toast.success("Delivery cancelled");
      setActiveDelivery(null);
      setPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel delivery");
    } finally {
      setLoading(false);
    }
  };

  // Handle photo upload/edit for existing delivery
  const handleHistoryPhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingDeliveryPhoto) return;
    
    setUploadingPhoto(true);
    try {
      const compressedImage = await compressImage(file, 200);
      
      await axios.patch(`${API}/deliveries/${editingDeliveryPhoto.id}/photo`, {
        photo: compressedImage
      });
      
      // Update local state
      setDeliveryHistory(prev => prev.map(d => 
        d.id === editingDeliveryPhoto.id 
          ? { ...d, photo: true, has_photo: true } 
          : d
      ));
      
      toast.success("Photo uploaded successfully!");
      setEditingDeliveryPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      // Reset input
      if (historyPhotoInputRef.current) {
        historyPhotoInputRef.current.value = '';
      }
    }
  };

  // Delete photo from existing delivery
  const handleDeleteDeliveryPhoto = async (deliveryId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    
    try {
      await axios.delete(`${API}/deliveries/${deliveryId}/photo`);
      
      // Update local state
      setDeliveryHistory(prev => prev.map(d => 
        d.id === deliveryId 
          ? { ...d, photo: false, has_photo: false } 
          : d
      ));
      
      toast.success("Photo deleted successfully!");
      setSelectedPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete photo");
    }
  };

  // View photo for a delivery (fetch on demand)
  const handleViewDeliveryPhoto = async (delivery) => {
    try {
      const response = await axios.get(`${API}/deliveries/${delivery.id}/photo`);
      if (response.data.photo) {
        setSelectedPhoto({ 
          photo: response.data.photo, 
          site: delivery.site_name, 
          date: delivery.checkin_time,
          deliveryId: delivery.id
        });
      } else {
        toast.error("Photo not found");
      }
    } catch (error) {
      toast.error("Failed to load photo");
    }
  };

  const selectedSite = sites.find(s => s.id === selectedSiteId);

  const openGoogleMaps = (site) => {
    // Encode the site address for URL
    const address = encodeURIComponent(site.address);
    
    // Create Google Maps URL
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${address}`;
    
    // For mobile devices, try to open the native app
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // For mobile, use location.href which doesn't get blocked
      window.location.href = googleMapsUrl;
    } else {
      // For desktop, use location.href as well to avoid popup blocker
      window.location.href = googleMapsUrl;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-white border-b-2 border-black/10 sticky top-0 z-50">
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded overflow-hidden flex items-center justify-center border border-black/10">
              <img 
                src="https://customer-assets.emergentagent.com/job_bcc851c7-6465-41a4-a49f-44dbf65b5fb7/artifacts/72dfskcd_unity-contractors-logo.jpg" 
                alt="Unity Contractors Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg uppercase leading-none" data-testid="app-title">
                UNITY DELIVERY
              </h1>
              <p className="text-xs font-mono text-muted-foreground">{user.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchData(); toast.success("Data synced!"); }}
              className="px-3 py-2 text-xs font-heading font-bold uppercase bg-green-500/20 text-green-600 hover:bg-green-500/30 border border-green-500/30 transition-colors flex items-center gap-2"
              data-testid="sync-data-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              SYNC
            </button>
            <button
              onClick={onLogout}
              className="p-2 hover:bg-black/5"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Tab navigation */}
        <div className="flex gap-2 border-b-2 border-black/10 -mx-4 px-4 -mt-4 pt-4 bg-white sticky top-[64px] z-40">
          <button
            onClick={() => setActiveTab("deliveries")}
            className={`px-4 h-11 font-heading font-bold uppercase text-sm border-b-2 -mb-[2px] flex items-center gap-2 transition-colors ${
              activeTab === "deliveries"
                ? "border-primary text-primary"
                : "border-transparent text-black/50 hover:text-black"
            }`}
            data-testid="driver-tab-deliveries"
          >
            <Truck className="w-4 h-4" strokeWidth={2} />
            DELIVERIES
          </button>
          <button
            onClick={() => setActiveTab("book-repair")}
            className={`px-4 h-11 font-heading font-bold uppercase text-sm border-b-2 -mb-[2px] flex items-center gap-2 transition-colors ${
              activeTab === "book-repair"
                ? "border-primary text-primary"
                : "border-transparent text-black/50 hover:text-black"
            }`}
            data-testid="driver-tab-book-repair"
          >
            <Wrench className="w-4 h-4" strokeWidth={2} />
            BOOK REPAIR
          </button>
        </div>

        {activeTab === "book-repair" ? (
          <RepairBookingTab user={user} />
        ) : (
        <>
        {dataLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground font-body">Loading...</p>
            </div>
          </div>
        ) : (
        <>
        {!activeDelivery && (
          <div className="bg-white border-2 border-black/10 p-6">
            <h2 className="font-heading font-bold text-xl uppercase mb-4">SELECT SITE & CHECK IN</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold font-body mb-3">SELECT SITE</label>
              
              {/* Search Bar */}
              <div className="relative mb-4">
                <input
                  type="text"
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  placeholder="Search site or customer..."
                  className="w-full h-12 pl-10 pr-10 bg-white border-2 border-black/10 focus:border-primary focus:outline-none font-body text-sm"
                  data-testid="site-search-input"
                />
                <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {siteSearch && (
                  <button
                    onClick={() => setSiteSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="clear-site-search"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              {filteredSites.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="font-body">No sites found matching &quot;{siteSearch}&quot;</p>
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {filteredSites.map((site) => (
                  <div key={site.id} className="relative">
                    <button
                      onClick={() => setSelectedSiteId(site.id)}
                      className={`w-full p-4 text-left border-2 transition-all ${
                        selectedSiteId === site.id
                          ? "border-primary bg-primary/5"
                          : "border-black/10 hover:border-black/20"
                      }`}
                      data-testid={`site-tab-${site.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" strokeWidth={2} />
                        <div className="min-w-0 flex-1 pr-8">
                          {site.customer_name && (
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{site.customer_name}</p>
                          )}
                          <p className="font-body font-semibold text-sm break-words">{site.name}</p>
                          {siteLoads[site.id] > 0 && (
                            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 border border-yellow-300 rounded" data-testid={`site-loads-badge-${site.id}`}>
                              <Truck className="w-3 h-3 text-yellow-700" strokeWidth={2} />
                              <span className="text-xs font-bold text-yellow-700">{siteLoads[site.id]} {siteLoads[site.id] === 1 ? 'load' : 'loads'}</span>
                            </div>
                          )}
                          {siteDumpingGrounds[site.id] && (
                            <p className="mt-1 text-xs text-cyan-700 font-body" data-testid={`site-dumping-${site.id}`}>
                              Dumping: {siteDumpingGrounds[site.id]}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openGoogleMaps(site);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-primary hover:bg-primary/90 text-white rounded-sm shadow-md transition-colors"
                      title="Open in Google Maps"
                      data-testid={`google-maps-button-${site.id}`}
                    >
                      <Navigation className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
              )}
            </div>

            {selectedSiteId && (
              <form onSubmit={handleCheckIn} className="space-y-4">
                <button
                  type="button"
                  onClick={() => setSelectedSiteId("")}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-2"
                  data-testid="back-to-sites-button"
                >
                  <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                  <span className="text-sm font-body">Back to site selection</span>
                </button>
                
                <div className="p-4 bg-black/5 border border-black/10 mb-4">
                  <p className="text-xs font-semibold font-body mb-1">SELECTED SITE:</p>
                  <p className="font-body font-bold">{selectedSite?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedSite?.address}</p>
                  {siteLoads[selectedSiteId] > 0 && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-yellow-100 border border-yellow-300 rounded" data-testid="selected-site-loads-badge">
                      <Truck className="w-4 h-4 text-yellow-700" strokeWidth={2} />
                      <span className="text-sm font-bold text-yellow-700">
                        {siteLoads[selectedSiteId]} {siteLoads[selectedSiteId] === 1 ? 'load' : 'loads'} remaining
                      </span>
                    </div>
                  )}
                  {siteDumpingGrounds[selectedSiteId] && (
                    <p className="mt-2 text-sm font-bold text-cyan-700" data-testid="selected-site-dumping-badge">
                      Dumping Ground: {siteDumpingGrounds[selectedSiteId]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold font-body mb-2">DELIVERY NOTES (OPTIONAL)</label>
                  <textarea
                    value={checkInData.delivery_notes}
                    onChange={(e) => setCheckInData({ ...checkInData, delivery_notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-transparent border-2 border-black/10 focus:border-primary focus:outline-none font-body text-sm"
                    placeholder="Any additional notes..."
                    data-testid="delivery-notes-input"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-12 bg-primary text-white font-heading font-bold uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-50"
                    data-testid="confirm-checkin-button"
                  >
                    {loading ? "CHECKING IN..." : "CONFIRM CHECK IN"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedSiteId("")}
                    className="px-6 h-12 bg-white border-2 border-black/20 font-heading font-bold uppercase hover:bg-black/5 transition-colors"
                    data-testid="cancel-site-button"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {activeDelivery && (
          <div className="space-y-4">
            <button
              onClick={handleCancelDelivery}
              disabled={loading}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="cancel-delivery-button"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
              <span className="text-sm font-body">Back to site selection</span>
            </button>
            
            <div className="bg-white border-2 border-black/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl uppercase">ACTIVE DELIVERY</h2>
                <span className="px-3 py-1 bg-accent-success/10 text-accent-success border border-accent-success/20 text-xs font-bold uppercase tracking-widest" data-testid="active-status-badge">
                  ACTIVE
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-1" strokeWidth={2} />
                  <div>
                    <p className="text-sm font-semibold font-body">SITE</p>
                    <p className="font-mono text-sm">{activeDelivery.site_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-primary mt-1" strokeWidth={2} />
                  <div>
                    <p className="text-sm font-semibold font-body">CHECK-IN TIME</p>
                    <p className="font-mono text-sm">{formatDateTimeDMY(activeDelivery.checkin_time)}</p>
                  </div>
                </div>

                {activeDelivery.delivery_notes && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-primary mt-1" strokeWidth={2} />
                    <div>
                      <p className="text-sm font-semibold font-body">NOTES</p>
                      <p className="font-body text-sm">{activeDelivery.delivery_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border-2 border-black/10 p-6">
              <h3 className="font-heading font-bold text-lg uppercase mb-4">COMPLETE DELIVERY</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold font-body mb-2 flex items-center gap-2">
                    <Camera className="w-4 h-4" strokeWidth={2} />
                    DELIVERY PHOTO
                  </label>
                  {!photo ? (
                    <>
                      {/* Hidden file input - completely off-screen for mobile compatibility */}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoCapture}
                        style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                        id="photo-input"
                        data-testid="photo-input"
                      />
                      {/* Clickable upload button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          photoInputRef.current?.click();
                        }}
                        className="w-full h-40 border-2 border-dashed border-black/20 flex items-center justify-center bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                        data-testid="photo-upload-area"
                      >
                        <div className="text-center pointer-events-none">
                          <Camera className="w-12 h-12 mx-auto mb-3 text-primary" strokeWidth={2} />
                          <p className="text-lg font-bold font-body text-black">TAP TO TAKE PHOTO</p>
                          <p className="text-xs text-gray-500 mt-1">Photo will be compressed automatically</p>
                        </div>
                      </button>
                    </>
                  ) : (
                    <div className="relative">
                      <img src={photo} alt="Delivery" className="w-full h-48 object-cover border-2 border-black/10" data-testid="photo-preview" />
                      <button
                        onClick={() => setPhoto(null)}
                        className="absolute top-2 right-2 px-3 py-1 bg-white border-2 border-black/20 text-xs font-bold uppercase hover:bg-black/5"
                        data-testid="remove-photo-button"
                      >
                        REMOVE
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleCheckOut}
                  disabled={loading || !photo}
                  className="w-full h-14 bg-primary text-white font-heading font-bold text-lg uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:opacity-50"
                  data-testid="checkout-button"
                >
                  {loading ? "CHECKING OUT..." : "COMPLETE & CHECK OUT"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delivery History Section - Only show when not in check-in/check-out flow */}
        {!activeDelivery && !selectedSiteId && (
          <div className="bg-white border-2 border-black/10 p-6">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between"
              data-testid="toggle-history-button"
            >
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-primary" strokeWidth={2} />
              <h2 className="font-heading font-bold text-lg uppercase">MY DELIVERY HISTORY</h2>
            </div>
            <span className="text-sm font-mono text-muted-foreground">
              {deliveryHistory.length} deliveries
            </span>
          </button>

          {showHistory && (
            <div className="mt-4 space-y-4">
              {/* Data Retention Notice */}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded">
                <Info className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                <span className="text-xs font-body">{DATA_RETENTION_TEXT}</span>
              </div>
              
              {deliveryHistory.length === 0 ? (
                <p className="text-center text-muted-foreground font-body py-8">No delivery history yet</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-black/10">
                          <th 
                            className="text-left py-2 font-semibold text-xs uppercase text-muted-foreground cursor-pointer hover:text-foreground select-none"
                            onClick={() => {
                              setHistorySortConfig(prev => ({
                                key: 'customer_name',
                                direction: prev.key === 'customer_name' && prev.direction === 'asc' ? 'desc' : 'asc'
                              }));
                              setHistoryPage(1);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              Customer
                              {historySortConfig.key === 'customer_name' ? (
                                historySortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                            </div>
                          </th>
                          <th 
                            className="text-left py-2 font-semibold text-xs uppercase text-muted-foreground cursor-pointer hover:text-foreground select-none"
                            onClick={() => {
                              setHistorySortConfig(prev => ({
                                key: 'site_name',
                                direction: prev.key === 'site_name' && prev.direction === 'asc' ? 'desc' : 'asc'
                              }));
                              setHistoryPage(1);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              Site
                              {historySortConfig.key === 'site_name' ? (
                                historySortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                            </div>
                          </th>
                          <th 
                            className="text-left py-2 font-semibold text-xs uppercase text-muted-foreground cursor-pointer hover:text-foreground select-none"
                            onClick={() => {
                              setHistorySortConfig(prev => ({
                                key: 'checkin_time',
                                direction: prev.key === 'checkin_time' && prev.direction === 'asc' ? 'desc' : 'asc'
                              }));
                              setHistoryPage(1);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              Check-in
                              {historySortConfig.key === 'checkin_time' ? (
                                historySortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                            </div>
                          </th>
                          <th 
                            className="text-left py-2 font-semibold text-xs uppercase text-muted-foreground cursor-pointer hover:text-foreground select-none"
                            onClick={() => {
                              setHistorySortConfig(prev => ({
                                key: 'status',
                                direction: prev.key === 'status' && prev.direction === 'asc' ? 'desc' : 'asc'
                              }));
                              setHistoryPage(1);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              Status
                              {historySortConfig.key === 'status' ? (
                                historySortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                            </div>
                          </th>
                          <th className="text-center py-2 font-semibold text-xs uppercase text-muted-foreground">
                            DO
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...deliveryHistory]
                          .sort((a, b) => {
                            const { key, direction } = historySortConfig;
                            let aVal = a[key] || '';
                            let bVal = b[key] || '';
                            if (key === 'checkin_time') {
                              aVal = new Date(aVal).getTime();
                              bVal = new Date(bVal).getTime();
                            } else {
                              aVal = String(aVal).toLowerCase();
                              bVal = String(bVal).toLowerCase();
                            }
                            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                            return 0;
                          })
                          .slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE)
                          .map((delivery) => (
                            <tr key={delivery.id} className="border-b border-black/5" data-testid={`history-row-${delivery.id}`}>
                              <td className="py-3 font-body">{delivery.customer_name || "-"}</td>
                              <td className="py-3 font-body">{delivery.site_name}</td>
                              <td className="py-3 font-mono text-xs">{formatDateDMY(delivery.checkin_time)}</td>
                              <td className="py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-bold uppercase ${
                                    delivery.status === "completed"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {delivery.status}
                                </span>
                              </td>
                              <td className="py-3">
                                <div className="flex items-center justify-center gap-1">
                                  {delivery.photo || delivery.has_photo ? (
                                    <>
                                      {/* View Photo */}
                                      <button
                                        onClick={() => handleViewDeliveryPhoto(delivery)}
                                        className="p-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded transition-colors"
                                        title="View Photo"
                                        data-testid={`view-photo-${delivery.id}`}
                                      >
                                        <ImageIcon className="w-4 h-4" strokeWidth={2} />
                                      </button>
                                      {/* Edit/Replace Photo */}
                                      <button
                                        onClick={() => {
                                          setEditingDeliveryPhoto(delivery);
                                          historyPhotoInputRef.current?.click();
                                        }}
                                        className="p-1.5 bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 rounded transition-colors"
                                        title="Replace Photo"
                                        data-testid={`edit-photo-${delivery.id}`}
                                      >
                                        <Edit2 className="w-4 h-4" strokeWidth={2} />
                                      </button>
                                      {/* Delete Photo */}
                                      <button
                                        onClick={() => handleDeleteDeliveryPhoto(delivery.id)}
                                        className="p-1.5 bg-red-500/10 text-red-600 hover:bg-red-500/20 rounded transition-colors"
                                        title="Delete Photo"
                                        data-testid={`delete-photo-${delivery.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                                      </button>
                                    </>
                                  ) : (
                                    /* Upload Photo */
                                    <button
                                      onClick={() => {
                                        setEditingDeliveryPhoto(delivery);
                                        historyPhotoInputRef.current?.click();
                                      }}
                                      className="p-1.5 bg-green-500/10 text-green-600 hover:bg-green-500/20 rounded transition-colors"
                                      title="Upload Photo"
                                      data-testid={`upload-photo-${delivery.id}`}
                                    >
                                      <Upload className="w-4 h-4" strokeWidth={2} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination for History */}
                  {deliveryHistory.length > ITEMS_PER_PAGE && (
                    <div className="flex items-center justify-between pt-4 border-t border-black/10">
                      <span className="text-sm text-muted-foreground font-body">
                        Page {historyPage} of {Math.ceil(deliveryHistory.length / ITEMS_PER_PAGE)}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                          disabled={historyPage === 1}
                          className="p-2 border-2 border-black/10 disabled:opacity-30"
                          data-testid="history-prev-page"
                        >
                          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => setHistoryPage(p => Math.min(Math.ceil(deliveryHistory.length / ITEMS_PER_PAGE), p + 1))}
                          disabled={historyPage >= Math.ceil(deliveryHistory.length / ITEMS_PER_PAGE)}
                          className="p-2 border-2 border-black/10 disabled:opacity-30"
                          data-testid="history-next-page"
                        >
                          <ChevronRight className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        )}
        </>
        )}

        {/* Photo View Modal */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div 
              className="bg-white max-w-lg w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-black/10">
                <div>
                  <h3 className="font-heading font-bold uppercase">Delivery Photo</h3>
                  <p className="text-sm text-muted-foreground">{selectedPhoto.site} • {formatDateDMY(selectedPhoto.date)}</p>
                </div>
                <button
                  onClick={() => setSelectedPhoto(null)}
                  className="p-2 hover:bg-black/5 rounded"
                  data-testid="close-photo-modal"
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
              <div className="p-4">
                <img 
                  src={selectedPhoto.photo} 
                  alt="Delivery Order" 
                  className="w-full h-auto"
                  data-testid="photo-modal-image"
                />
              </div>
              {/* Action buttons for photo management */}
              {selectedPhoto.deliveryId && (
                <div className="flex gap-2 p-4 border-t border-black/10">
                  <button
                    onClick={() => {
                      setEditingDeliveryPhoto({ id: selectedPhoto.deliveryId });
                      historyPhotoInputRef.current?.click();
                    }}
                    className="flex-1 h-10 bg-blue-500 text-white font-heading font-bold text-sm uppercase flex items-center justify-center gap-2"
                    data-testid="modal-replace-photo"
                  >
                    <Edit2 className="w-4 h-4" strokeWidth={2} />
                    REPLACE
                  </button>
                  <button
                    onClick={() => handleDeleteDeliveryPhoto(selectedPhoto.deliveryId)}
                    className="flex-1 h-10 bg-red-500 text-white font-heading font-bold text-sm uppercase flex items-center justify-center gap-2"
                    data-testid="modal-delete-photo"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2} />
                    DELETE
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hidden file input for history photo uploads */}
        <input
          ref={historyPhotoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleHistoryPhotoCapture}
          style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          data-testid="history-photo-input"
        />

        {/* Upload loading overlay */}
        {uploadingPhoto && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="font-heading font-bold text-sm uppercase">Uploading Photo...</p>
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
